/**
 * Willtec Universal Carousel Engine (<willtec-carousel>)
 * Single unified Web Component powering all carousels across the theme:
 * - Brands ("Navegue por marca")
 * - Popular Categories & Lines ("Categorias mais procuradas")
 * - Testimonials ("Veja o que nossos clientes falam")
 * - Blog ("Blog Willtec")
 * - Flash Sale & Product Carousels
 *
 * Features:
 * - Proportional page calculation & smooth CSS scroll-snap.
 * - Dynamic dots generation & state sync.
 * - Prev / Next navigation buttons with aria state management.
 * - Automatic passive resize listeners.
 */
(() => {
  if (customElements.get('willtec-carousel')) return;

  class WilltecCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.prevBtn = this.querySelector('[data-carousel-prev]');
    this.nextBtn = this.querySelector('[data-carousel-next]');
    this.dotsContainer = this.querySelector('[data-carousel-dots]');

    if (!this.track) return;

    this.bindNavigation();
    this.renderDots();
    this.updateControls();

    this.track.addEventListener('scroll', () => this.updateControls(), { passive: true });
    window.addEventListener('resize', () => {
      this.renderDots();
      this.updateControls();
    }, { passive: true });
  }

  bindNavigation() {
    if (this.prevBtn) {
      this.prevBtn.onclick = (e) => {
        e.preventDefault();
        this.scrollPage(-1);
      };
    }

    if (this.nextBtn) {
      this.nextBtn.onclick = (e) => {
        e.preventDefault();
        this.scrollPage(1);
      };
    }
  }

  scrollPage(direction) {
    const pageWidth = this.track.clientWidth;
    this.track.scrollBy({
      left: direction * pageWidth,
      behavior: 'smooth'
    });
  }

  scrollToPageIndex(pageIndex) {
    const maxScrollLeft = this.track.scrollWidth - this.track.clientWidth;
    if (maxScrollLeft <= 0 || !this.dots || this.dots.length <= 1) return;

    const targetScroll = (pageIndex / (this.dots.length - 1)) * maxScrollLeft;
    this.track.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  }

  renderDots() {
    if (!this.dotsContainer) return;

    // Direct dots provided in HTML markup (e.g. product carousel)
    const existingDots = this.querySelectorAll('[data-carousel-dot]');
    if (existingDots.length > 0 && !this.dotsContainer.hasAttribute('data-[#dynamic]')) {
      this.dots = existingDots;
      this.dots.forEach((dot, index) => {
        dot.onclick = (e) => {
          e.preventDefault();
          this.scrollToPageIndex(index);
        };
      });
      return;
    }

    // Dynamic dots generation
    this.dotsContainer.innerHTML = '';

    const totalScrollWidth = this.track.scrollWidth;
    const clientWidth = this.track.clientWidth;
    if (clientWidth <= 0) return;

    const maxScroll = totalScrollWidth - clientWidth;
    if (maxScroll <= 5) return; // Only 1 page

    const totalPages = Math.ceil(totalScrollWidth / clientWidth);

    for (let i = 0; i < totalPages; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.dataset.carouselDot = i;
      dot.className = 'w-4 h-4 rounded-full border border-[#818181] bg-transparent hover:border-[#FE3B40] transition-all duration-200 focus:outline-none';
      dot.setAttribute('aria-label', `Ir para página ${i + 1}`);
      dot.onclick = (e) => {
        e.preventDefault();
        this.scrollToPageIndex(i);
      };
      this.dotsContainer.appendChild(dot);
    }
    this.dots = this.dotsContainer.querySelectorAll('[data-carousel-dot]');
  }

  updateControls() {
    const scrollLeft = this.track.scrollLeft;
    const maxScrollLeft = this.track.scrollWidth - this.track.clientWidth;

    if (this.prevBtn) {
      const isStart = scrollLeft <= 5;
      this.prevBtn.disabled = isStart;
      this.prevBtn.setAttribute('aria-disabled', isStart ? 'true' : 'false');
      this.prevBtn.classList.toggle('opacity-40', isStart);
      this.prevBtn.classList.toggle('cursor-not-allowed', isStart);
    }

    if (this.nextBtn) {
      const isEnd = scrollLeft >= maxScrollLeft - 5;
      this.nextBtn.disabled = isEnd;
      this.nextBtn.setAttribute('aria-disabled', isEnd ? 'true' : 'false');
      this.nextBtn.classList.toggle('opacity-40', isEnd);
      this.nextBtn.classList.toggle('cursor-not-allowed', isEnd);
    }

    if (this.dots && this.dots.length > 0 && maxScrollLeft > 5) {
      const ratio = scrollLeft / maxScrollLeft;
      const activePageIndex = Math.min(
        this.dots.length - 1,
        Math.max(0, Math.round(ratio * (this.dots.length - 1)))
      );

      this.dots.forEach((dot, idx) => {
        if (idx === activePageIndex) {
          dot.className = 'w-4 h-4 rounded-full bg-[#FE3B40] border border-[#FE3B40] transition-all duration-200 focus:outline-none';
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.className = 'w-4 h-4 rounded-full bg-transparent border border-[#818181] hover:border-[#FE3B40] transition-all duration-200 focus:outline-none';
          dot.removeAttribute('aria-current');
        }
      });
    }
  }
}

if (!customElements.get('willtec-carousel')) {
  customElements.define('willtec-carousel', WilltecCarousel);
}
// Aliases for legacy component tag names ensuring 100% backward compatibility
if (!customElements.get('willtec-brands')) {
  customElements.define('willtec-brands', class extends WilltecCarousel {});
}
if (!customElements.get('willtec-popular-categories')) {
  customElements.define('willtec-popular-categories', class extends WilltecCarousel {});
}
if (!customElements.get('willtec-testimonials')) {
  customElements.define('willtec-testimonials', class extends WilltecCarousel {});
}
if (!customElements.get('willtec-blog-carousel')) {
  customElements.define('willtec-blog-carousel', class extends WilltecCarousel {});
}
  if (!customElements.get('willtec-product-carousel')) {
    customElements.define('willtec-product-carousel', class extends WilltecCarousel {});
  }
})();
